import { SlideUpdate } from 'api/slides';
import { CaseUpdate } from 'api/study';
import { concat, filter, find, flatMap, forEach, includes, isEmpty, map, omit, some } from 'es-toolkit/compat';
import { Procedure } from 'interfaces/procedure';

import { assignManifestDraftCaseIds, ManifestCaseTarget, ManifestDialogUrlState } from './dialogState';
import {
  applyManifestDraftPatches,
  collectManifestCaseLabelErrors,
  combineManifestCasesWithExistingCases,
  mergeManifestCases,
  removeSlidesFromManifestCases,
  resolveManifestExistingCaseAssignments,
} from './manifestResolution';

export const applyManifestSlideEdits = (
  previousState: ManifestDialogUrlState,
  changes: Partial<SlideUpdate>,
  slideIds: string[]
): ManifestDialogUrlState => {
  const nextSlidePatches = { ...(previousState.slidePatchesById || {}) };
  forEach(slideIds, (slideId) => {
    nextSlidePatches[slideId] = { ...(nextSlidePatches[slideId] || {}), ...changes };
  });
  return { ...previousState, slidePatchesById: nextSlidePatches };
};

export const applyManifestCaseEdits = ({
  previousState,
  changes,
  caseIds,
  currentDraftCases,
  baseManifestCases,
  isCaseLabelEditable,
}: {
  previousState: ManifestDialogUrlState;
  changes: Partial<CaseUpdate>;
  caseIds: number[];
  currentDraftCases: Procedure[];
  baseManifestCases: Procedure[];
  isCaseLabelEditable: (slideId: string) => boolean;
}): ManifestDialogUrlState => {
  const nextCasePatches = { ...(previousState.casePatchesById || {}) };
  const { label, ...metadataChanges } = changes;
  forEach(caseIds, (caseId) => {
    if (!isEmpty(metadataChanges)) {
      nextCasePatches[caseId] = { ...(nextCasePatches[caseId] || {}), ...metadataChanges };
    }
    const displayedCase = find(currentDraftCases, { id: caseId });
    forEach(baseManifestCases, (rawCase) => {
      if (
        some(rawCase.slides, (slide) => isCaseLabelEditable(slide.id) && some(displayedCase?.slides, { id: slide.id }))
      ) {
        nextCasePatches[rawCase.id] = {
          ...nextCasePatches[rawCase.id],
          ...metadataChanges,
          ...(label === undefined ? {} : { label }),
        };
      }
    });
  });
  return { ...previousState, casePatchesById: nextCasePatches };
};

export const addManifestDraftCases = (
  previousState: ManifestDialogUrlState,
  casesToAdd: Procedure[],
  assignToExistingCases: boolean
): ManifestDialogUrlState => {
  const addedSlideIds = map(flatMap(casesToAdd, 'slides'), 'id');
  const newDraftCases = assignManifestDraftCaseIds({
    cases: casesToAdd,
    retainedCases: concat(previousState.uploadedManifestCases || [], previousState.manualManifestCases || []),
    casePatchesById: previousState.casePatchesById,
  });
  const nextExistingCaseAssignments = { ...(previousState.existingCaseAssignmentsBySlideId || {}) };
  forEach(addedSlideIds, (slideId) => {
    nextExistingCaseAssignments[slideId] = assignToExistingCases ? null : 'new';
  });
  return {
    ...previousState,
    existingCaseAssignmentsBySlideId: nextExistingCaseAssignments,
    pendingSlidesSelectionAction: undefined,
    removedSlideIds: filter(previousState.removedSlideIds || [], (slideId) => !includes(addedSlideIds, slideId)),
    // Removal only masks stored rows, so re-added slides must leave their original rows before the mask is cleared.
    uploadedManifestCases: removeSlidesFromManifestCases(previousState.uploadedManifestCases || [], addedSlideIds),
    manualManifestCases: concat(
      removeSlidesFromManifestCases(previousState.manualManifestCases || [], addedSlideIds),
      newDraftCases
    ),
  };
};

export const resolveManifestDraftCases = ({
  manifestCases,
  existingProcedures,
  overrideCases,
  targetsBySlideId,
}: {
  manifestCases: Procedure[];
  existingProcedures: Procedure[];
  overrideCases: boolean;
  targetsBySlideId?: Readonly<Record<string, ManifestCaseTarget | undefined>>;
}) => {
  if (!isEmpty(collectManifestCaseLabelErrors(manifestCases, existingProcedures))) return manifestCases;
  const mergedManifestCases = mergeManifestCases([], manifestCases);

  // A draft case the user explicitly targeted at "New case" must not absorb the slides of a saved case that shares its label.
  const isExplicitNewDraftCase = (manifestCase: Procedure) =>
    !some(existingProcedures, { id: manifestCase.id }) &&
    some(manifestCase.slides, (slide) => targetsBySlideId?.[slide.id] === 'new');

  return overrideCases
    ? mergedManifestCases
    : combineManifestCasesWithExistingCases(mergedManifestCases, existingProcedures, isExplicitNewDraftCase).cases;
};

export const resolveManifestDraftAssignments = ({
  cases,
  uploadedManifestCases,
  existingProcedures,
  assignmentsBySlideId,
  casePatchesById,
  slidePatchesById,
  labId,
  studyId,
}: Parameters<typeof resolveManifestExistingCaseAssignments>[0] & {
  uploadedManifestCases: Procedure[];
  slidePatchesById: Record<string, Partial<SlideUpdate>>;
}) => {
  const patchedManifestCases = applyManifestDraftPatches(cases, casePatchesById, slidePatchesById);
  const targetCasePatches = { ...casePatchesById };
  const importedTargets = new Set<number>();
  forEach(patchedManifestCases, (procedure) => {
    if (!some(uploadedManifestCases, { id: procedure.id })) return;
    const target = find(existingProcedures, { label: procedure.label });
    if (
      !target ||
      importedTargets.has(target.id) ||
      !some(procedure.slides, (slide) => assignmentsBySlideId[slide.id] === target.id)
    )
      return;
    importedTargets.add(target.id);
    targetCasePatches[target.id] = {
      ...omit(procedure, ['id', 'slides', 'label']),
      ...casePatchesById[target.id],
    };
  });
  return resolveManifestExistingCaseAssignments({
    cases: patchedManifestCases,
    existingProcedures,
    assignmentsBySlideId,
    casePatchesById: targetCasePatches,
    labId,
    studyId,
  });
};
